/**
 * Which policies are still owed acknowledgements, worst first.
 *
 * The index, not the evidence. An administrator uses this to decide who to
 * chase; the per-policy page behind each row is the thing shown to a surveyor.
 *
 * A table, a page at a time, counted and ordered by the server. Four totals
 * above it that are also the four ways of narrowing it, and a search. Deliberately nothing else: no charts, no bulk actions.
 */

import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { isCallFailure } from '../../lib/callable';
import { useDebounced } from '../search/useDebounced';
import { formatDate } from '../policies/policyTable';
import { FILTER_MAX } from '../../types/fieldLimits';
import { NAMED_MAX, PAGE_SIZES, useAckOverview, type AckRow, type AckTotals, type AckView, type PageSize } from './useAckOverview';
import styles from '../../shared/Page.module.css';
import table from './Admin.module.css';
import own from './AdminAcknowledgements.module.css';

const n = (v: number) => v.toLocaleString('en-US');

/** The four totals, which are also the four views of the table. */
const TOTALS: { view: AckView; label: string; value: (t: AckTotals) => number; note?: (t: AckTotals) => string | null }[] = [
  { view: 'all', label: 'Assigned', value: (t) => t.assigned, note: (t) => `across ${n(t.policies)} ${t.policies === 1 ? 'policy' : 'policies'}` },
  { view: 'complete', label: 'Completed', value: (t) => t.completed, note: (t) => (t.assigned > 0 ? `${Math.floor((t.completed / t.assigned) * 100)}% of assigned` : null) },
  { view: 'outstanding', label: 'Outstanding', value: (t) => t.outstanding, note: () => 'not yet acknowledged' },
  { view: 'overdue', label: 'Overdue', value: (t) => t.overdue, note: (t) => (t.overdue > 0 ? `in ${n(t.policiesOverdue)} ${t.policiesOverdue === 1 ? 'policy' : 'policies'}` : 'nothing is late') },
];

const VIEW_HEADING: Record<AckView, string> = {
  all: 'Every policy that asks for acknowledgement',
  outstanding: 'Policies with acknowledgements outstanding',
  overdue: 'Policies with overdue acknowledgements',
  complete: 'Policies everybody has acknowledged',
};
const VIEW_EMPTY: Record<AckView, string> = {
  all: 'No published policy asks staff for an acknowledgement yet.',
  outstanding: 'Nothing is outstanding. Every acknowledgement asked for has been given.',
  overdue: 'No acknowledgement is overdue.',
  complete: 'No policy has been acknowledged by everybody it was assigned to yet.',
};

/** What the last column says about a row, in words first. */
export function rowStanding(r: Pick<AckRow, 'overdue' | 'outstanding' | 'earliestDue'>): { text: string; tone: 'danger' | 'plain' | 'ok' } {
  if (r.overdue > 0) return { text: `${n(r.overdue)} overdue`, tone: 'danger' };
  if (r.outstanding > 0) return { text: r.earliestDue ? `Due ${formatDate(new Date(r.earliestDue).toISOString().slice(0, 10))}` : 'Outstanding', tone: 'plain' };
  return { text: 'Complete', tone: 'ok' };
}

function SearchBox({ value, onChange }: { value: string; onChange: (text: string) => void }) {
  const id = useId();
  const [typed, setTyped] = useState(value);
  // The address changed from outside (Back, Clear): the box follows it.
  const [seen, setSeen] = useState(value);
  if (seen !== value) { setSeen(value); setTyped(value); }
  const settled = useDebounced(typed, 250);
  useEffect(() => {
    if (settled.trim() !== value.trim() && settled === typed) onChange(settled);
  }, [settled]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className={table.search}>
      <label className="vh" htmlFor={id}>Search policies</label>
      <input id={id} type="search" maxLength={FILTER_MAX} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Search every policy, including document text" />
    </div>
  );
}

export function AdminAcknowledgementsPage() {
  const { session } = useAuth();
  const data = useAckOverview(session?.orgId ?? null);
  const sizeId = useId();
  const { state } = data;

  if (data.error && data.rows.length === 0 && !data.isLoading) {
    return (
      <PageState title="Could not load the report" tone="alert">
        {isCallFailure(data.error) ? data.error.message : 'Something went wrong.'}{' '}
        <button type="button" className={styles.btn} onClick={data.retry}>Try again</button>
      </PageState>
    );
  }

  const filtered = state.view !== 'all' || data.searching;
  const range = data.rows.length === 0 ? ''
    : data.searching ? `${n(data.rows.length)} ${data.rows.length === 1 ? 'policy' : 'policies'}`
      : `${n(data.from)}–${n(data.to)} of ${n(data.total ?? 0)}`;

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>Acknowledgements</h1>
        <p className={styles.subtitle}>
          Where each policy stands, the ones furthest behind first. Open a policy to see exactly who has
          acknowledged it and who has not: that is the report to show a surveyor.
        </p>
      </header>

      <div className={styles.body}>
        {/* The totals are of the whole organization, whatever the table is narrowed to, so they do not shift as you click them. */}
        <div className={own.totals} role="group" aria-label="Acknowledgements across the organization">
          {TOTALS.map((t) => {
            const on = state.view === t.view;
            return (
              <button key={t.view} type="button" className={on ? `${own.total} ${own.totalOn}` : own.total} aria-pressed={on} onClick={() => data.change({ view: t.view })}>
                <span className={own.totalLabel}>{t.label}</span>
                <span className={`${own.totalValue} tnum ${t.view === 'overdue' && (data.totals?.overdue ?? 0) > 0 ? own.danger : ''}`}>
                  {data.totals ? n(t.value(data.totals)) : '…'}
                </span>
                <span className={own.totalNote}>{data.totals ? t.note?.(data.totals) ?? '' : ''}</span>
              </button>
            );
          })}
        </div>

        <div className={own.filters}>
          <SearchBox value={state.text} onChange={(text) => data.change({ text }, { replace: true })} />
          {filtered && (
            <button type="button" className={own.linkBtn} onClick={() => data.change({ view: 'all', text: '' })}>Clear filters</button>
          )}
          <span className={own.spacer} />
          <span className={table.count} role="status" aria-live="polite">{data.isUpdating ? 'Loading…' : range && <b>{range}</b>}</span>
        </div>

        <h2 className={own.heading}>
          {data.searching ? `Policies matching "${state.text.trim()}"` : VIEW_HEADING[state.view]}
        </h2>
        {data.searching && state.view !== 'all' && <p className={own.note}>Only those in: {TOTALS.find((t) => t.view === state.view)!.label.toLowerCase()}.</p>}
        {data.searchCut && <p className={own.note}>Showing the {NAMED_MAX} best matches. Add a word to narrow it.</p>}

        {data.isLoading ? <Spinner label="Loading acknowledgements" /> : data.pastTheEnd ? (
          <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">
            This list is shorter than it was when you reached this page.{' '}
            <button type="button" className={own.linkBtn} style={{ padding: 0 }} onClick={data.first}>Go to the first page</button>
          </div>
        ) : data.rows.length === 0 ? (
          <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">
            {data.searching ? `No policy that asks for acknowledgement matches "${state.text.trim()}"${state.view !== 'all' ? ` in ${TOTALS.find((t) => t.view === state.view)!.label.toLowerCase()}` : ''}.`
              : VIEW_EMPTY[state.view]}
          </div>
        ) : (
          <div className={table.scroller} aria-busy={data.isUpdating || undefined}>
            <table className={`${table.table} ${own.table}`}>
              <caption className="vh">{VIEW_HEADING[state.view]}, most overdue first</caption>
              <thead>
                <tr>
                  <th scope="col">Number</th>
                  <th scope="col">Policy</th>
                  <th scope="col">Department</th>
                  <th scope="col" className={own.num}>Assigned</th>
                  <th scope="col" className={own.num}>Completed</th>
                  <th scope="col" className={own.num}>Outstanding</th>
                  <th scope="col" className={own.num}>Overdue</th>
                  <th scope="col">Standing</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const standing = rowStanding(r);
                  return (
                    <tr key={r.policyId}>
                      <td className={own.number}>{r.policyNumber || <span className={styles.absent}>&mdash;</span>}</td>
                      <td>
                        <Link to={`/admin/acknowledgements/${r.policyId}`} state={data.hereState} className={table.link}>{r.policyTitle}</Link>
                      </td>
                      <td className={own.dept}>{r.departmentNames.length ? r.departmentNames.join(', ') : <span className={styles.absent}>&mdash;</span>}</td>
                      <td className={`${own.num} tnum`}>{n(r.assigned)}</td>
                      <td className={`${own.num} tnum`}>
                        {n(r.completed)}
                        <span className={own.percent}>{r.assigned > 0 ? `${Math.floor((r.completed / r.assigned) * 100)}%` : ''}</span>
                      </td>
                      <td className={`${own.num} tnum`}>{r.outstanding > 0 ? n(r.outstanding) : <span className={styles.absent}>&mdash;</span>}</td>
                      <td className={`${own.num} tnum`}>
                        {r.overdue > 0 ? <strong className={own.danger}>{n(r.overdue)}</strong> : <span className={styles.absent}>&mdash;</span>}
                      </td>
                      <td><span className={standing.tone === 'danger' ? `${own.standing} ${own.standingDanger}` : standing.tone === 'ok' ? `${own.standing} ${own.standingOk}` : own.standing}>{standing.text}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!data.searching && (data.rows.length > 0 || data.hasPrevious) && (
          <nav className={own.pager} aria-label="Pages of policies">
            <span className={own.pageSize}>
              <label htmlFor={sizeId}>Rows per page</label>
              <select id={sizeId} className={own.select} value={state.size} onChange={(e) => data.change({ size: Number(e.target.value) as PageSize })}>
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </span>
            <button type="button" className={own.pageBtn} disabled={!data.hasPrevious || data.isUpdating} onClick={data.previous}>Previous</button>
            <button type="button" className={own.pageBtn} disabled={!data.hasNext || data.isUpdating} onClick={data.next}>Next</button>
          </nav>
        )}
      </div>
    </>
  );
}

